import ThemeToggle from "../theme/ThemeToggle";
import React, { useEffect, useState } from "react";
import { Menu, X, Phone, Globe } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import BrandLogo from "./BrandLogo";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const LANGS = [
  { code: "en", label: "English", short: "EN" },
  { code: "pt", label: "Português", short: "PT" },
  { code: "es", label: "Español", short: "ES" },
];

const Navbar = ({ onQuoteClick }) => {
  const { t, lang, setLang } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "services", label: t.nav.services },
    { id: "why", label: t.nav.why },
    { id: "projects", label: t.nav.projects },
    { id: "reviews", label: t.nav.reviews },
  ];

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 safe-px ${
        scrolled
          ? "backdrop-blur-xl bg-black/75 border-b border-white/5"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16 sm:h-20">
        <a
          href="/#top"
          data-testid="brand-logo"
          className="flex items-center gap-2.5 sm:gap-3 group"
        >
          <BrandLogo size="h-11 w-11 sm:h-16 sm:w-16" className="transition-transform group-hover:scale-105" />
          <div className="leading-tight">
            <div className="font-display font-bold text-white text-[13px] sm:text-sm tracking-wide">
              XAVIER ASSEMBLY
            </div>
            <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.25em] text-sky-400">
              Outdoor & Gym Pro
            </div>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-10">
          {links.map((l) => (
            <a
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              href={l.href || `/#${l.id}`}
              className="text-sm font-medium text-zinc-300 hover:text-white transition-colors relative group"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 w-0 h-px bg-sky-400 transition-all group-hover:w-full"></span>
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <ThemeToggle compact testId="header-theme-toggle" />
          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="language-selector"
              className="hidden sm:flex items-center gap-2 text-xs text-zinc-300 hover:text-white border border-white/10 px-3 py-2 transition-colors hover:border-sky-400"
            >
              <Globe size={14} />
              {LANGS.find((l) => l.code === lang)?.short}
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="bg-zinc-900 border border-white/10 rounded-none min-w-[160px]"
            >
              {LANGS.map((l) => (
                <DropdownMenuItem
                  key={l.code}
                  data-testid={`lang-option-${l.code}`}
                  onClick={() => setLang(l.code)}
                  className={`text-sm cursor-pointer rounded-none ${
                    lang === l.code
                      ? "bg-sky-500/10 text-sky-400"
                      : "text-zinc-200"
                  }`}
                >
                  {l.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <a
            href="tel:+15105079318"
            data-testid="nav-call-button"
            className="hidden md:inline-flex items-center gap-2 text-xs font-semibold text-white hover:text-sky-400 transition-colors"
          >
            <Phone size={14} />
            (510) 507-9318
          </a>

          <button
            data-testid="nav-quote-button"
            onClick={onQuoteClick}
            className="hidden md:inline-flex btn-primary !py-2.5 !px-5 !text-xs"
          >
            {t.nav.cta}
          </button>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden text-white p-2 -mr-2 h-11 w-11 flex items-center justify-center"
            aria-label={{ en: "Toggle menu", pt: "Abrir ou fechar menu", es: "Abrir o cerrar menú" }[lang] || "Toggle menu"}
            aria-expanded={open}
            aria-controls="mobile-menu-panel"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          id="mobile-menu-panel"
          data-testid="mobile-menu"
          className="lg:hidden bg-zinc-950 border-t border-white/5 px-5 py-6 space-y-1 safe-pb"
        >
          {links.map((l) => (
            <a
              key={l.id}
              data-testid={`mobile-nav-link-${l.id}`}
              href={l.href || `/#${l.id}`}
              onClick={() => setOpen(false)}
              className="block text-base font-medium text-zinc-200 hover:text-sky-400 py-3.5 border-b border-white/5"
            >
              {l.label}
            </a>
          ))}
          <label className="flex min-h-[44px] items-center justify-between gap-3 py-3 text-sm text-zinc-200">
            {{ en: "Language", pt: "Idioma", es: "Idioma" }[lang] || "Language"}
            <select data-testid="mobile-language-selector" value={lang} onChange={e => setLang(e.target.value)} className="min-h-[44px] rounded border border-white/25 bg-zinc-900 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-sky-400">
              {LANGS.map(language => <option key={language.code} value={language.code}>{language.label}</option>)}
            </select>
          </label>
          <div className="pt-4 flex flex-col gap-3">
            <a
              href="tel:+15105079318"
              data-testid="mobile-call-button"
              className="btn-secondary w-full justify-center"
              onClick={() => setOpen(false)}
            >
              <Phone size={14} />
              (510) 507-9318
            </a>
            <button
              data-testid="mobile-quote-button"
              onClick={() => {
                setOpen(false);
                onQuoteClick();
              }}
              className="btn-primary w-full justify-center"
            >
              {t.nav.cta}
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
